import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const money = (v) => `$${Number(v || 0).toFixed(0)}`;

function BudgetRow({ r, editing, value, onChange }) {
  const pct = r.pct ?? 0;
  const color = pct >= 100 ? "bg-palm" : pct >= 60 ? "bg-gold" : "bg-crimson";
  return (
    <div className="space-y-1" data-testid={`budget-row-${r.account_id}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold">{r.name}{!r.active && <span className="ml-1 text-xs text-muted-foreground">(inactive)</span>}</span>
        {editing ? (
          <Input type="number" min="0" step="1" className="h-7 w-28 text-right" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`budget-input-${r.account_id}`} />
        ) : (
          <span className="font-mono-accent text-xs" data-testid={`budget-figures-${r.account_id}`}>{money(r.actual)} / {r.target > 0 ? money(r.target) : "no target"}{r.pct !== null && ` · ${r.pct}%`}</span>
        )}
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-parchment-card">
        <div className={`h-full rounded-full transition-[width] duration-700 ${color}`} style={{ width: `${r.target > 0 ? pct : 0}%` }} />
      </div>
    </div>
  );
}

export function AccountBudgets() {
  const year = new Date().getFullYear();
  const { data, reload } = useApi(`/admin/budgets?year=${year}`);
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState({});
  useEffect(() => { if (data) setVals(Object.fromEntries(data.accounts.map((r) => [r.account_id, r.target || ""]))); }, [data]);
  const save = async () => {
    try {
      await api.put("/admin/budgets", { year, targets: Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, parseFloat(v) || 0])) });
      toast.success("Budget targets saved"); setEditing(false); reload();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-10 glass rounded-2xl p-6" data-testid="account-budgets">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><Target size={18} className="text-gold" /> {year} budget targets</h3>
        {editing ? (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="budget-cancel">Cancel</Button>
            <Button size="sm" onClick={save} className="bg-crimson" data-testid="budget-save">Save targets</Button>
          </div>
        ) : <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="budget-edit">Set targets</Button>}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Yearly income goal per account vs confirmed payments so far.</p>
      <div className="mt-4 space-y-4">
        {(data?.accounts || []).map((r) => <BudgetRow key={r.account_id} r={r} editing={editing} value={vals[r.account_id] ?? ""} onChange={(v) => setVals({ ...vals, [r.account_id]: v })} />)}
        {data?.unassigned > 0 && <p className="text-xs text-muted-foreground" data-testid="budget-unassigned">{money(data.unassigned)} in unassigned payments not counted toward any target — tag them in Reconciliation.</p>}
      </div>
    </div>
  );
}
